import { ArrowBackOutlined, Close } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { listMessages } from "../../../actions/util";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { ESRState, Thread } from "../../../types/data";
import { DrawerContainer } from "../DrawerContainer";
import { NewThreadEntry } from "./NewThreadEntry";
import { ThreadSummaryItem } from "./ThreadSummaryItem";

type Props = {
	threads: Thread[];
	open: boolean;
	onClose: () => void;
	onSelectThread: (threadId: number) => void;
};

export const ThreadList: FC<Props> = ({
	threads,
	open,
	onClose,
	onSelectThread,
}) => {
	const { esrId = "", activeId } = useParams();
	const dispatch = useDispatch();
	const [user] = useCurrentUser();
	const [threadFilter, setThreadFilter] = useState<string>("all");
	const uniqueLabels = useMemo<string[]>(() => {
		if (!Array.isArray(threads) || threads.length === 0) return [];
		const seen = new Set<string>();
		setThreadFilter("all");
		for (const t of threads) {
			const label = t.addendum_label?.trim() ? t.addendum_label : "initial";
			if (!seen.has(label)) seen.add(label);
		}
		const arr = Array.from(seen);
		arr.sort((a, b) => {
			if (a == "initial") return -1;
			if (b === "initial") return 1;
			return a.localeCompare(b, undefined, { sensitivity: "base" });
		});
		return arr;
	}, [threads]);

	const filteredThreads = useMemo(() => {
		if (!Array.isArray(threads)) return [];
		if (threadFilter === "all") return threads;
		if (threadFilter === "initial")
			return threads.filter((t) => (t.addendum_label ?? "").trim() === "");
		return threads.filter(
			(thread) => (thread.addendum_label ?? "").trim() === threadFilter,
		);
	}, [threads, threadFilter]);
	useEffect(() => {
		if (activeId && open) dispatch(listMessages(activeId));
	}, [open, activeId]);

	const send_message_exit = useSelector(
		(state: ESRState) => state.sending_message_progress.exit,
	);
	const send_message_inprogress = useSelector(
		(state: ESRState) => state.sending_message_progress.inProgress,
	);
	const send_message_complete = useSelector(
		(state: ESRState) => state.sending_message_progress.complete,
	);

	return (
		<>
			{send_message_exit && (
				<DrawerContainer open={open} onClose={onClose}>
					<Box
						sx={{
							width: 500,
							height: "100%",
							background: "rgba(0, 62, 98, 0.02)",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								height: 60,
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								px: "24px",
								justifyContent: "space-between",
								background: "#003E6205",
							}}
						>
							<Button
								variant="text"
								startIcon={<ArrowBackOutlined />}
								onClick={onClose}
							>
								back
							</Button>
							<Typography fontWeight={500} fontSize={14}>
								Problems
							</Typography>
							<IconButton aria-label="close threads" onClick={onClose}>
								<Close />
							</IconButton>
						</Box>

						<Box sx={{ flex: 1, overflow: "scroll" }}>
							{uniqueLabels.length > 0 ? (
								<Box
									sx={{
										border: "1px solid #0000001F",
										p: "20px 16px",
										display: "wrap",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "space-between",
										m: 1,
									}}
								>
									<Button
										size="small"
										variant={threadFilter === "all" ? "contained" : "outlined"}
										onClick={() => setThreadFilter("all")}
										sx={{ m: 0.5 }}
									>
										All
									</Button>

									{uniqueLabels.map((label) => (
										<Button
											key={label}
											size="small"
											variant={
												threadFilter === label ? "contained" : "outlined"
											}
											onClick={() => setThreadFilter(label)}
											sx={{ whiteSpace: "nowrap", m: 0.5 }}
											title={label}
										>
											{label !== "initial" ? label : esrId}
										</Button>
									))}
								</Box>
							) : (
								<></>
							)}
							{threads && filteredThreads ? (
								filteredThreads.map((thread) => (
									<ThreadSummaryItem
										key={thread.id}
										setCurrentThread={onSelectThread}
										{...thread}
									/>
								))
							) : (
								<></>
							)}
						</Box>
						{user && <NewThreadEntry user={user} />}
					</Box>
				</DrawerContainer>
			)}

			{send_message_inprogress && (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						width: "100vw",
						height: "100vh",
						zIndex: 1400, // above drawers, modals, etc.
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backdropFilter: "blur(6px)",
						backgroundColor: "rgba(255, 255, 255, 0.4)", // optional overlay tint
					}}
				>
					<Box
						sx={{
							textAlign: "center",
							padding: 4,
						}}
					>
						<CircularProgress sx={{ mb: 2 }} />
						<Typography variant="h6">Sending message…</Typography>
					</Box>
				</Box>
			)}

			{(!send_message_exit && !send_message_inprogress && send_message_complete) && (
				<>
					<Box
						sx={{
							position: "fixed",
							top: 0,
							left: 0,
							width: "100vw",
							height: "100vh",
							zIndex: 1400, // above drawers, modals, etc.
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backdropFilter: "blur(6px)",
							backgroundColor: "rgba(255, 255, 255, 0.4)", // optional overlay tint
						}}
					>
						<Box
							sx={{
								textAlign: "center",
								padding: 4,
							}}
						>
							<Typography align="center">✅ Message sent!</Typography>
						</Box>
					</Box>
				</>
			)}
		</>
	);
};
